/**
 * solicitacoes-compra-store — Solicitação de compra interna (Round D / D5a).
 *
 * Hooks TanStack Query pro `/api/v1/solicitacoes-compra` (gated por
 * COMPRAS_ENABLED + requireAuth). Formulário interno SEM preço. A "fila" (todas)
 * é só do Compras. ZERO localStorage.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type SolicitacaoCompraUrgencia = 'baixa' | 'media' | 'alta';
export type SolicitacaoCompraStatus = 'aberta' | 'classificada' | 'concluida' | 'cancelada';
/** FX-8: alvo do vínculo da solicitação. */
export type TipoVinculo = 'projeto' | 'deal' | 'cliente' | 'interno';
export interface VinculoSolicitacao {
  tipoVinculo: TipoVinculo;
  projetoId?: string | null;
  dealId?: string | null;
  clienteId?: string | null;
  setorId?: string | null;
  categoriaId?: string | null;
}

export interface SolicitacaoCompraItem {
  id: string;
  solicitacaoId: string;
  item: string;
  quantidade: number;
  linkReferencia: string | null;
  anexoFotoUrl: string | null;
  ordem: number;
}
export interface SolicitacaoCompra {
  id: string;
  solicitanteUserId: string;
  solicitanteNome: string;
  solicitanteDepartamentos: string;
  obra: string | null;
  urgencia: SolicitacaoCompraUrgencia;
  prazo: string | null;
  observacao: string | null;
  status: SolicitacaoCompraStatus;
  /** FX-20: número sequencial humano (#N). */
  numeroHumano: number | null;
  // D5b — classificação pelo Compras.
  projetoId: string | null;
  usoInterno: boolean;
  classificadoPor: string | null;
  classificadoEmIso: string | null;
  // FX-8 — vínculo da solicitação (rastreabilidade).
  tipoVinculo: TipoVinculo | null;
  dealId: string | null;
  clienteId: string | null;
  setorId: string | null;
  categoriaId: string | null;
  /** 🔴 #347 (mig 633): o motivo, quem e quando do cancelamento. null = não cancelada (ou antes da mig 633). */
  canceladaMotivo?: string | null;
  canceladaPorNome?: string | null;
  canceladaEmIso?: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
}
export interface SolicitacaoCompraFull extends SolicitacaoCompra {
  itens: SolicitacaoCompraItem[];
}
/** D5b — item de solicitação p/ ligar na cotação (com contexto). */
export interface SolicitacaoItemParaCotacao {
  id: string;
  item: string;
  quantidade: number;
  solicitacaoId: string;
  solicitanteNome: string;
  obra: string | null;
  projetoId: string | null;
  usoInterno: boolean;
}
export interface SolicitacaoCompraContexto {
  nome: string;
  departamentos: string[];
  podeInformarObra: boolean;
}
export interface CreateSolicitacaoCompraInput {
  obra?: string | null;
  urgencia?: SolicitacaoCompraUrgencia;
  prazo?: string | null;
  observacao?: string | null;
  /** FX-8: vínculo escolhido pelo solicitante. */
  vinculo?: VinculoSolicitacao;
  itens: Array<{ item: string; quantidade?: number; linkReferencia?: string | null }>;
}

const KEY = ['solicitacoes-compra'] as const;
const BASE = '/api/v1/solicitacoes-compra';

export function useSolicitacaoContexto() {
  return useQuery({
    queryKey: [...KEY, 'contexto'] as const,
    queryFn: () => api.get<SolicitacaoCompraContexto>(`${BASE}/me/contexto`),
    enabled: hasToken(),
  });
}

export function useMinhasSolicitacoes() {
  return useQuery({
    queryKey: [...KEY, 'minhas'] as const,
    queryFn: () => api.get<{ items: SolicitacaoCompra[] }>(`${BASE}/minhas`),
    enabled: hasToken(),
  });
}

// Fase 2 (16/09/2026): `useFilaSolicitacoes` morreu com a aba "Fila do Compras" — a Fila fundiu no
// Quadro ("A classificar" é coluna de lá). Quem quer ver os pedidos usa `useQuadroCompras`.

/** D5b — itens de solicitações abertas/classificadas p/ ligar na cotação (Compras). */
export function useSolicitacaoItensParaCotacao(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'itens-cotacao'] as const,
    queryFn: () => api.get<{ items: SolicitacaoItemParaCotacao[] }>(`${BASE}/itens-cotacao`),
    enabled: enabled && hasToken(),
  });
}

export function useSolicitacaoActions() {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: KEY });
  const criar = useMutation({
    mutationFn: (input: CreateSolicitacaoCompraInput) => api.post<SolicitacaoCompraFull>(BASE, input),
    onSuccess: inv,
  });
  // 🔴 #347: cancelar aceita MOTIVO (fica na solicitação, no Quadro e na trilha).
  const cancelar = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string | null }) =>
      api.patch<SolicitacaoCompra>(`${BASE}/${id}/cancelar`, { motivo: motivo?.trim() || null }),
    onSuccess: inv,
  });
  const classificar = useMutation({
    mutationFn: ({
      id,
      vinculo,
      projetoId,
      usoInterno,
    }: {
      id: string;
      vinculo?: VinculoSolicitacao;
      projetoId?: string | null;
      usoInterno?: boolean;
    }) =>
      api.patch<SolicitacaoCompra>(
        `${BASE}/${id}/classificar`,
        // FX-8: prefere o vínculo completo; cai pro legado se não houver.
        vinculo ? { vinculo } : { projetoId: projetoId ?? null, usoInterno: !!usoInterno },
      ),
    onSuccess: inv,
  });
  return { criar, cancelar, classificar };
}

// ─── FU-2 — quadro de Compras (card-espelho + situação dos itens) ─────────

export type ItemSituacao = 'a_cotar' | 'em_cotacao' | 'comprado' | 'recebido' | 'entregue';

export interface QuadroCompraItem {
  id: string;
  item: string;
  quantidade: number;
  situacao: ItemSituacao;
  /** 🔴 18/09 (o buraco): o aviso do levantamento para este item (mudou a quantidade, saiu, sobrou). null = nada. */
  aviso?: string | null;
}
export interface QuadroCompraCard {
  /** = cardDealId — o KanbanBoard universal usa `item.id` pro drag. */
  id: string;
  solicitacaoId: string;
  cardDealId: string;
  fase: string;
  titulo: string;
  /** FX-20: número sequencial humano (#N) da solicitação. */
  numeroHumano: number | null;
  /** Solicitante (snapshot) — faixa de metadados do card (FX-7). */
  solicitanteNome: string | null;
  projetoId: string | null;
  projetoNome: string | null;
  /** Nome curto do cliente (fantasia > razão social) — FX-5/FX-7. */
  clienteNome: string | null;
  /** Prazo (texto livre) — faixa de metadados (FX-7). */
  prazo: string | null;
  interno: boolean;
  urgencia: SolicitacaoCompraUrgencia;
  /** FX-6: cotação à qual os itens já estão ligados. null = ainda não cotado. */
  cotacaoId: string | null;
  /** 🔴 #347: a cancelada fica no Quadro, MARCADA, e não se arrasta. null = não cancelada. */
  cancelada?: { motivo: string | null; porNome: string | null; emIso: string | null } | null;
  /** 🔴 18/09: quantos itens têm aviso do levantamento. */
  avisos?: number;
  /** 🔴 #365: "esta obra tem outras N solicitações" (as não canceladas), com os números. null = nenhuma outra. */
  mesmaObra?: { outras: number; numeros: number[] } | null;
  itens: QuadroCompraItem[];
}

export const QUADRO_KEY = [...KEY, 'quadro'] as const;

/** Os cards-espelho do quadro Compras (Compras/admin). */
export function useQuadroCompras() {
  return useQuery({
    queryKey: QUADRO_KEY,
    queryFn: () => api.get<{ items: QuadroCompraCard[] }>(`${BASE}/quadro`),
    enabled: hasToken(),
  });
}

/** Ações do quadro: muda a situação de um item (recalcula a coluna do card). */
export function useQuadroActions() {
  const qc = useQueryClient();
  const invalidarQuadro = () => qc.invalidateQueries({ queryKey: QUADRO_KEY });
  const setItemSituacao = useMutation({
    mutationFn: ({ itemId, situacao }: { itemId: string; situacao: ItemSituacao }) =>
      api.patch<{ items: QuadroCompraCard[] }>(`${BASE}/itens/${itemId}/situacao`, { situacao }),
    onSuccess: (data) => {
      qc.setQueryData(QUADRO_KEY, data);
    },
  });
  return { setItemSituacao, invalidarQuadro };
}
